import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, X, Send, ArrowLeft, User, Clock,
  Briefcase, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MessagesPanel = ({ token, isOpen, onClose, initialRecipient = null }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchConversations();
      
      // Si hay un destinatario inicial (desde networking), iniciar chat
      if (initialRecipient) {
        startNewConversation(initialRecipient);
      }
    }
  }, [isOpen, token, initialRecipient]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (conversationId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/messages/conversation/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages);
      setOtherUser(response.data.other_user);
      
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      toast.error('Error al cargar conversación');
    } finally {
      setLoading(false);
    }
  };

  const startNewConversation = async (recipient) => {
    // Check if conversation already exists
    const existingConv = conversations.find(c => 
      c.participants.includes(recipient.id)
    );
    
    if (existingConv) {
      openConversation(existingConv.id);
    } else {
      // Start new conversation view
      setSelectedConversation({ isNew: true });
      setMessages([]);
      setOtherUser({
        id: recipient.id,
        name: recipient.full_name || recipient.name,
        type: recipient.user_type || recipient.type,
        photo: recipient.profile_photo || recipient.photo
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !otherUser) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/api/messages/send`,
        {
          recipient_id: otherUser.id,
          content: newMessage.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add message to local state
      const newMsg = {
        id: response.data.message_id,
        content: newMessage.trim(),
        sender_id: 'me',
        created_at: new Date().toISOString()
      };
      setMessages([...messages, newMsg]);
      setNewMessage('');

      // If this was a new conversation, set the conversation ID
      if (selectedConversation?.isNew) {
        setSelectedConversation({ id: response.data.conversation_id });
      }

      // Refresh conversations
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="messages-panel">
      <div className="bg-gray-900 border border-purple-500/30 rounded-2xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {selectedConversation ? (
            <>
              <button
                onClick={() => {
                  setSelectedConversation(null);
                  setMessages([]);
                  setOtherUser(null);
                }}
                className="text-white/60 hover:text-white mr-3"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
                  {otherUser?.photo ? (
                    <img src={otherUser.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-sm gotham-bold">
                      {otherUser?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="gotham-bold text-white">{otherUser?.name}</h3>
                  <p className="text-white/40 text-xs flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {otherUser?.type === 'author' ? 'Autor' : 'Audio-Lector'}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              Mensajes
            </h2>
          )}
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {selectedConversation ? (
          // Messages View
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-white/40">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Inicia la conversación</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isMe = msg.sender_id === 'me' || msg.sender_id !== otherUser?.id;
                  return (
                    <div
                      key={msg.id || index}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          isMe
                            ? 'bg-purple-600 text-white rounded-br-md'
                            : 'bg-white/10 text-white rounded-bl-md'
                        }`}
                      >
                        <p className="verdana-regular text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-purple-200' : 'text-white/40'}`}>
                          {formatDate(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 bg-black/30 border border-purple-500/30 rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="btn-viubux px-4"
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </>
        ) : (
          // Conversations List
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-white/60">Cargando...</div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="mb-2">No tienes conversaciones</p>
                <p className="text-sm">Visita el Networking para conectar con otros miembros</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => openConversation(conv.id)}
                    className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex items-center gap-3"
                    data-testid={`conversation-${conv.id}`}
                  >
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white gotham-bold">
                        {conv.other_user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="gotham-bold text-white truncate">
                          {conv.other_user?.name}
                        </h3>
                        <span className="text-white/40 text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(conv.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-white/60 text-sm truncate">
                          {conv.last_message || 'Nueva conversación'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-white/30 text-xs mt-1">
                        {conv.other_user?.type === 'author' ? 'Autor' : 'Audio-Lector'}
                      </p>
                    </div>

                    <ChevronRight className="w-5 h-5 text-white/30" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPanel;
